import {
  DROP_COLLECT_TIME,
  DROP_DRIFT,
  DROP_LIFETIME,
  DROP_RING_BAND,
  DROP_TAP_RADIUS,
  R_PLANET,
  R_SPAWN,
} from '../constants';
import { angleDist, clamp } from '../polar';
import { randRange } from '../rng';
import { acquireDrop, juiceBonusFor, magnetRadius, pushEvent } from '../world';
import type { Drop, World } from '../types';

/**
 * Kills drop juice, and juice have to be picked up. That's the active layer:
 * a wave you fought well but never harvested leaves you unable to upgrade.
 *
 * Three ways to collect, in ascending order of effort:
 *   - the planet's magnet, if you bought it (free, short range)
 *   - the orbit ring sweeping across a juice (free, but only where the ring is)
 *   - tapping it (always works, costs attention)
 *
 * The ring being a collector is the load-bearing decision here. Tap-to-collect
 * on its own would compete with reading bearings; letting rotation harvest
 * means the spin you were making anyway also pays for itself.
 */
export function spawnDrop(world: World, theta: number, r: number, value: number): Drop | null {
  if (value <= 0) return null;
  const d = acquireDrop(world);
  if (!d) return null;

  d.alive = true;
  d.theta = theta;
  d.r = clamp(r, R_PLANET + 6, R_SPAWN + 20);
  d.value = value;
  d.life = DROP_LIFETIME;
  d.maxLife = DROP_LIFETIME;
  d.vR = randRange(world.rng, DROP_DRIFT * 0.5, DROP_DRIFT);
  d.collected = false;
  d.collectT = 0;
  return d;
}

export function updateDrops(world: World, dt: number): void {
  const magnet = magnetRadius(world);
  const ringR = world.ringR;

  for (const d of world.drops) {
    if (!d.alive) continue;

    // Already picked up: run out the pickup animation, then release the slot.
    if (d.collected) {
      d.collectT += dt;
      if (d.collectT >= DROP_COLLECT_TIME) d.alive = false;
      continue;
    }

    // Drift outward, slowing as it goes, so drops spread off the crust.
    d.r = clamp(d.r + d.vR * dt, R_PLANET + 6, R_SPAWN + 20);
    d.vR *= Math.exp(-2.2 * dt);

    if (magnet > 0 && d.r - R_PLANET <= magnet) {
      collect(world, d);
      continue;
    }
    if (Math.abs(d.r - ringR) <= DROP_RING_BAND) {
      collect(world, d);
      continue;
    }

    d.life -= dt;
    if (d.life <= 0) {
      d.alive = false;
      world.stats.juiceLost += d.value;
      pushEvent(world, { type: 'drop-lost', theta: d.theta, r: d.r, amount: d.value });
    }
  }
}

function collect(world: World, d: Drop): void {
  const gain = d.value + juiceBonusFor(world);
  world.juice += gain;
  world.stats.juiceEarned += gain;
  d.collected = true;
  d.collectT = 0;
  pushEvent(world, { type: 'collect', theta: d.theta, r: d.r, amount: gain });
}

/**
 * A tap on the field. Picks up the nearest juice within a fat radius — the
 * target is generous on purpose, because missing a juice you clearly aimed at
 * feels like the game cheating rather than a skill check.
 */
export function collectAt(world: World, theta: number, r: number): boolean {
  let best: Drop | null = null;
  let bestDist = Infinity;

  for (const d of world.drops) {
    if (!d.alive || d.collected) continue;
    // Straight-line distance between two polar points, near enough for a tap.
    const dr = d.r - r;
    const dTheta = angleDist(d.theta, theta) * ((d.r + r) * 0.5);
    const dist = Math.sqrt(dr * dr + dTheta * dTheta);
    if (dist <= DROP_TAP_RADIUS && dist < bestDist) {
      bestDist = dist;
      best = d;
    }
  }

  if (!best) return false;
  collect(world, best);
  return true;
}

/** Sweep every remaining juice into the purse — used on wave clear. */
export function collectAll(world: World): void {
  for (const d of world.drops) {
    if (d.alive && !d.collected) collect(world, d);
  }
}
